/**
 * Agent Connection Context
 * Manages connections between agents for multi-agent collaboration
 */

import { createContext, useContext, useState, ReactNode } from 'react';

export type AgentRole = 
  | 'Leadership'
  | 'Workforce Development'
  | 'Legal'
  | 'Finance'
  | 'HR'
  | 'Operations'
  | 'Marketing'
  | 'IT';

export interface AgentMessage {
  id: string;
  agentRole: AgentRole;
  content: string;
  timestamp: Date;
}

export interface ConnectedAgent {
  role: AgentRole;
  isConnected: boolean;
}

interface AgentConnectionContextType {
  // Connected agents
  connectedAgents: ConnectedAgent[];
  toggleAgentConnection: (role: AgentRole) => void;
  
  // Broadcasting
  broadcastQuestion: (question: string, fromAgent: AgentRole) => void;
  agentResponses: AgentMessage[];
  clearResponses: () => void;
  
  // Connection state
  isAgentConnected: (role: AgentRole) => boolean;
  getConnectedAgents: () => AgentRole[];
}

const AgentConnectionContext = createContext<AgentConnectionContextType | undefined>(undefined);

export function AgentConnectionProvider({ children }: { children: ReactNode }) {
  const [connectedAgents, setConnectedAgents] = useState<ConnectedAgent[]>([
    { role: 'Leadership', isConnected: true }, // Always connected
    { role: 'Workforce Development', isConnected: false },
    { role: 'Legal', isConnected: false },
    { role: 'Finance', isConnected: false },
    { role: 'HR', isConnected: false },
    { role: 'Operations', isConnected: false },
    { role: 'Marketing', isConnected: false },
    { role: 'IT', isConnected: false },
  ]);

  const [agentResponses, setAgentResponses] = useState<AgentMessage[]>([]);

  const toggleAgentConnection = (role: AgentRole) => {
    if (role === 'Leadership') return; // Can't disconnect Leadership agent
    
    setConnectedAgents(prev =>
      prev.map(agent =>
        agent.role === role
          ? { ...agent, isConnected: !agent.isConnected }
          : agent
      )
    );
  };

  const broadcastQuestion = (question: string, fromAgent: AgentRole) => {
    const connected = connectedAgents.filter(a => a.isConnected && a.role !== fromAgent);
    
    // Simulate responses from connected agents
    const responses: AgentMessage[] = connected.map(agent => ({
      id: `${agent.role}-${Date.now()}-${Math.random()}`,
      agentRole: agent.role,
      content: generateMockResponse(agent.role, question),
      timestamp: new Date(),
    }));

    setAgentResponses(responses);
  };

  const clearResponses = () => {
    setAgentResponses([]);
  };

  const isAgentConnected = (role: AgentRole): boolean => {
    return connectedAgents.find(a => a.role === role)?.isConnected || false;
  };

  const getConnectedAgents = (): AgentRole[] => {
    return connectedAgents.filter(a => a.isConnected).map(a => a.role);
  };

  return (
    <AgentConnectionContext.Provider
      value={{
        connectedAgents,
        toggleAgentConnection,
        broadcastQuestion,
        agentResponses,
        clearResponses,
        isAgentConnected,
        getConnectedAgents,
      }}
    >
      {children}
    </AgentConnectionContext.Provider>
  );
}

export function useAgentConnection() {
  const context = useContext(AgentConnectionContext);
  if (!context) {
    throw new Error('useAgentConnection must be used within AgentConnectionProvider');
  }
  return context;
}

// Mock response generator
function generateMockResponse(role: AgentRole, question: string): string {
  const responses: Record<AgentRole, string> = {
    'Leadership': `From a leadership perspective: ${question}`,
    'Workforce Development': `Workforce development recommends: Focus on employee training and development programs.`,
    'Legal': `Legal considerations: Ensure compliance with all relevant regulations and policies.`,
    'Finance': `Financial analysis: Review budget impact and ROI projections before proceeding.`,
    'HR': `HR perspective: Consider impact on employee morale, retention, and company culture.`,
    'Operations': `Operations insight: Assess workflow efficiency and resource allocation requirements.`,
    'Marketing': `Marketing viewpoint: Evaluate brand alignment and customer communication strategy.`,
    'IT': `IT assessment: Review technical requirements, security implications, and system integration.`,
  };

  return responses[role] || `Response from ${role} agent.`;
}
